import React from "react";
import styles from "../../../styles/AdminConsole.module.css";
import { formatDate } from "../../../utils/utils";
import { FABRIC_CATEGORIES, TRADE_ROLES } from "../../../data/constants";
import Toggle from "../shared/components/Toggle";
import SelectWrapper from "../shared/components/SelectWrapper";

const SAMPLE_STAGES = ["requested", "in-production", "delivered", "approved"];
const SAMPLE_LABELS = {
  requested: "Requested",
  "in-production": "In Production",
  delivered: "Delivered",
  approved: "Approved",
};

const fabricClass = (cat, styles) => {
  if (cat === "knitted") return styles.cat_knitted;
  if (cat === "woven") return styles.cat_woven;
  if (cat === "non-woven") return styles.cat_nonwoven;
  return styles.cat_common;
};

const tradeClass = (role, styles) => {
  const map = {
    retail: styles.cat_merchandiser,
    importer: styles.cat_logistics,
    wholesale: styles.cat_designer,
    technical: styles.cat_qualitycontrol,
  };
  return map[role] || styles.cat_common;
};

/**
 * LeadRow
 *
 * Wrapped in React.memo — only re-renders when its own lead object or
 * the active editing/confirm IDs change.
 *
 * Props:
 *   lead                  object
 *   isAdmin               bool
 *   canWrite              bool
 *   writeAllowed          bool   (canWrite && lead.status === "approved")
 *   supplierUsers         array  (admin only)
 *   deleteConfirmId       string | null
 *   editingMeetingId      string | null
 *   meetingDateInput      string
 *   meetingLocationInput  string
 *
 *   — handlers —
 *   onToggleField           (leadId, field, currentVal, extra?) => void
 *   onSampleChange          (lead, newStatus) => void
 *   onOpenLogContact        (lead) => void
 *   onOpenSampleLog         (lead) => void
 *   onOpenEmailsPanel       (lead) => void
 *   onOpenEdit              (lead) => void
 *   onDelete                (lead) => void
 *   onApprove               (lead) => void
 *   onReject                (lead) => void
 *   onCancelDelete          (lead) => void   ← merchandiser cancels their own request
 *   onAssignSupplier        (leadId, supplierUid) => void
 *   onViewContactLog        (lead) => void
 *   onSetEditingMeeting     (leadId) => void
 *   onMeetingDateChange     (val) => void
 *   onMeetingLocationChange (val) => void
 *   onSaveMeeting           (leadId) => void
 */
const LeadRow = React.memo(function LeadRow({
  lead,
  isAdmin,
  canWrite,
  writeAllowed,
  supplierUsers,
  deleteConfirmId,
  editingMeetingId,
  meetingDateInput,
  meetingLocationInput,
  onToggleField,
  onSampleChange,
  onOpenLogContact,
  onOpenSampleLog,
  onOpenEmailsPanel,
  onOpenEdit,
  onDelete,
  onApprove,
  onReject,
  onCancelDelete,
  onAssignSupplier,
  onViewContactLog,
  onSetEditingMeeting,
  onMeetingDateChange,
  onMeetingLocationChange,
  onSaveMeeting,
}) {
  // A delete-requested lead has status === "delete" in Firestore.
  // The row is dimmed to signal it's pending admin action.
  const isDeleteRequested = lead.status === "delete";

  return (
    <tr
      className={styles.tableRow}
      style={
        isDeleteRequested
          ? { opacity: 0.55, background: "rgba(200,80,80,0.05)" }
          : {}
      }
    >
      {/* Buyer */}
      <td className={styles.td} style={{ fontWeight: 600 }}>
        {lead.buyerName}
      </td>

      {/* Created By */}
      <td
        className={styles.td}
        style={{ fontSize: 11, color: "var(--text-muted)" }}
      >
        {lead.createdByName || lead.ownerName}
      </td>

      {/* Contact */}
      <td className={styles.td}>{lead.contactPerson}</td>

      {/* Emails */}
      <td className={styles.td}>
        {(lead.buyerEmails || []).length > 0 ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {lead.buyerEmails.map((e) => (
              <a
                key={e}
                href={`mailto:${e}`}
                style={{
                  fontSize: 10,
                  color: "var(--gold)",
                  textDecoration: "none",
                }}
              >
                {e}
              </a>
            ))}
          </div>
        ) : (
          <span style={{ opacity: 0.35 }}>—</span>
        )}
        {writeAllowed && (
          <button
            className={styles.editBtn}
            style={{ marginTop: 4, fontSize: 9 }}
            onClick={() => onOpenEmailsPanel(lead)}
          >
            ✎
          </button>
        )}
      </td>

      {/* Country */}
      <td className={styles.td}>{lead.country}</td>

      {/* Fabric */}
      <td className={styles.td}>
        <span
          className={`${styles.categoryBadge} ${fabricClass(lead.fabricCategory, styles)}`}
        >
          {lead.fabricCategory}
        </span>
      </td>

      {/* Trade Role */}
      <td className={styles.td}>
        <span
          className={`${styles.categoryBadge} ${tradeClass(lead.tradeRole, styles)}`}
        >
          {lead.tradeRole}
        </span>
      </td>

      {/* Tryouts */}
      <td className={styles.td}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span>{lead.tryouts || 0}</span>
          {writeAllowed && (
            <button
              className={styles.editBtn}
              onClick={() => onOpenLogContact(lead)}
              title="Log contact attempt"
            >
              +
            </button>
          )}
        </div>
      </td>

      {/* Last Contact */}
      <td className={styles.td}>
        <button
          style={{
            background: "none",
            border: "none",
            padding: 0,
            cursor: lead.lastContactDate ? "pointer" : "default",
            color: lead.lastContactDate ? "var(--gold)" : "inherit",
            fontSize: 11,
          }}
          onClick={() => lead.lastContactDate && onViewContactLog(lead)}
          title={lead.lastContactDate ? "View contact log" : undefined}
        >
          {lead.lastContactDate ? formatDate(lead.lastContactDate) : "—"}
        </button>
      </td>

      {/* Meeting */}
      <td className={styles.td}>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <Toggle
            label="Confirmed"
            checked={!!lead.meetingConfirmed}
            disabled={!writeAllowed}
            onChange={() =>
              onToggleField(lead.id, "meetingConfirmed", lead.meetingConfirmed)
            }
          />
          <Toggle
            label="Done"
            checked={!!lead.meetingDone}
            disabled={!writeAllowed}
            onChange={() =>
              onToggleField(lead.id, "meetingDone", lead.meetingDone)
            }
          />
          {lead.meetingConfirmed && (
            <div
              style={{
                fontSize: 10,
                color: "var(--text-muted)",
                lineHeight: 1.6,
              }}
            >
              {editingMeetingId === lead.id ? (
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 4 }}
                >
                  <input
                    type="date"
                    className={styles.input}
                    style={{ fontSize: 10, padding: "4px 8px" }}
                    value={meetingDateInput}
                    onChange={(e) => onMeetingDateChange(e.target.value)}
                  />
                  <input
                    type="text"
                    className={styles.input}
                    placeholder="Location"
                    style={{ fontSize: 10, padding: "4px 8px" }}
                    value={meetingLocationInput}
                    onChange={(e) => onMeetingLocationChange(e.target.value)}
                  />
                  <button
                    className={`${styles.btn} ${styles.btnPrimary}`}
                    style={{ padding: "4px 10px", fontSize: 9, marginTop: 0 }}
                    onClick={() => onSaveMeeting(lead.id)}
                  >
                    Save
                  </button>
                </div>
              ) : (
                <span
                  style={{ cursor: writeAllowed ? "pointer" : "default" }}
                  onClick={() => {
                    if (!writeAllowed) return;
                    onSetEditingMeeting(lead);
                  }}
                >
                  {lead.meetingDate ? formatDate(lead.meetingDate) : "Set date"}
                  {lead.meetingLocation ? ` · ${lead.meetingLocation}` : ""}
                </span>
              )}
            </div>
          )}
        </div>
      </td>

      {/* Sample */}
      <td className={styles.td}>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {writeAllowed ? (
            <SelectWrapper
              value={lead.sampleStatus || ""}
              style={{ fontSize: 10, padding: "5px 24px 5px 8px" }}
              wrapperClassName={styles.sampleSelect}
              onChange={(e) => onSampleChange(lead, e.target.value || null)}
            >
              <option value="">— None —</option>
              {SAMPLE_STAGES.map((s) => (
                <option key={s} value={s}>
                  {SAMPLE_LABELS[s]}
                </option>
              ))}
            </SelectWrapper>
          ) : (
            <span style={{ fontSize: 10 }}>
              {lead.sampleStatus ? SAMPLE_LABELS[lead.sampleStatus] : "—"}
            </span>
          )}
          {lead.sampleStatus && (
            <button
              className={styles.editBtn}
              style={{ fontSize: 9, alignSelf: "flex-start" }}
              onClick={() => onOpenSampleLog(lead)}
            >
              📋 Log
            </button>
          )}
        </div>
      </td>

      {/* Converted */}
      <td className={styles.td}>
        <Toggle
          checked={!!lead.orderConverted}
          disabled={!writeAllowed}
          hint={lead.orderConverted ? "Yes" : "No"}
          onChange={() =>
            onToggleField(
              lead.id,
              "orderConverted",
              lead.orderConverted,
              lead.orderConverted
                ? { orderPaid: false, orderPaymentPaid: false }
                : {},
            )
          }
        />
      </td>

      {/* Payment */}
      <td className={styles.td}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 4,
            minWidth: 110,
          }}
        >
          {lead.meetingDone && (
            <Toggle
              label="Phase 1"
              checked={!!lead.meetingPaymentPaid}
              disabled={!isAdmin}
              hint={lead.meetingPaymentPaid ? "Yes" : "No"}
              onChange={() =>
                isAdmin &&
                onToggleField(
                  lead.id,
                  "meetingPaymentPaid",
                  lead.meetingPaymentPaid,
                )
              }
            />
          )}
          {lead.orderPaid && (
            <Toggle
              label="Phase 2"
              checked={!!lead.orderPaymentPaid}
              disabled={!isAdmin}
              hint={lead.orderPaymentPaid ? "Yes" : "No"}
              onChange={() =>
                isAdmin &&
                onToggleField(
                  lead.id,
                  "orderPaymentPaid",
                  lead.orderPaymentPaid,
                )
              }
            />
          )}
          {!lead.meetingDone && !lead.orderPaid && (
            <span style={{ opacity: 0.3, fontSize: 10 }}>—</span>
          )}
        </div>
      </td>

      {/* Supplier — admin only */}
      {isAdmin && (
        <td className={styles.td}>
          <SelectWrapper
            value={lead.potentialSupplierUid || ""}
            style={{ fontSize: 10, padding: "6px 28px 6px 10px" }}
            wrapperClassName={styles.supplierSelect}
            onChange={(e) => onAssignSupplier(lead.id, e.target.value)}
          >
            <option value="">— Assign —</option>
            {(supplierUsers || [])
              .filter((s) => s.category === lead.fabricCategory)
              .map((s) => (
                <option key={s.uid} value={s.uid}>
                  {s.name}
                </option>
              ))}
          </SelectWrapper>
        </td>
      )}

      {/* Actions — admin approve/reject only (delete requests handled in LeadsTab) */}
      {isAdmin && (
        <td className={styles.td}>
          {(lead.status === "pending" || lead.status === "flagged") && (
            <div style={{ display: "flex", gap: 6 }}>
              <button
                className={`${styles.btn} ${styles.btnPrimary}`}
                style={{ padding: "5px 12px", fontSize: 9, marginTop: 0 }}
                onClick={() => onApprove(lead)}
              >
                Approve
              </button>
              <button
                className={styles.btn}
                style={{
                  padding: "5px 12px",
                  fontSize: 9,
                  color: "#e08080",
                  borderColor: "rgba(220,100,100,0.3)",
                }}
                onClick={() => onReject(lead)}
              >
                Reject
              </button>
            </div>
          )}
          {/* Delete-requested leads show a read-only label here;
              the actual Confirm / Dismiss lives in the Leads Tab */}
          {isDeleteRequested && (
            <span style={{ fontSize: 9, color: "#e08080", opacity: 0.75 }}>
              Delete requested
            </span>
          )}
        </td>
      )}

      {/* Edit */}
      {canWrite && (
        <td className={styles.td}>
          {/* Hide edit button while a delete request is pending */}
          {!isDeleteRequested && (
            <button
              className={styles.editBtn}
              onClick={() => onOpenEdit(lead)}
              title="Edit lead"
            >
              ✎
            </button>
          )}
        </td>
      )}

      {/* Delete / Cancel delete request */}
      {canWrite && (
        <td className={styles.td}>
          {isDeleteRequested && !isAdmin ? (
            /* Merchandiser can cancel their own pending delete request */
            <button
              className={styles.editBtn}
              style={{
                fontSize: 9,
                color: "var(--text-muted)",
                borderColor: "rgba(180,180,180,0.3)",
              }}
              onClick={() => onCancelDelete(lead)}
              title="Cancel delete request"
            >
              Cancel
            </button>
          ) : (
            /* Normal delete / request-delete button */
            !isDeleteRequested && (
              <button
                className={styles.editBtn}
                style={{
                  color: deleteConfirmId === lead.id ? "#e05555" : undefined,
                  borderColor:
                    deleteConfirmId === lead.id
                      ? "rgba(220,80,80,0.4)"
                      : undefined,
                }}
                onClick={() => onDelete(lead)}
                title={isAdmin ? "Delete lead" : "Request deletion"}
              >
                {deleteConfirmId === lead.id ? "Sure?" : "🗑"}
              </button>
            )
          )}
        </td>
      )}
    </tr>
  );
});

export default LeadRow;
